import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { useEntitlements } from '../../context/EntitlementsContext';
import { PLAN_LIMIT_EVENT, type PlanLimitDetail } from '../../api/client';
import { ENTITLEMENT_LABELS, entitlementKey } from '../../api/entitlements';
import { plansCopy } from '../../copy/plans';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { canManageBilling, currentPlanSentence, fill, formatResetDate } from './upgradeText';

// The one place a refused request turns into an upgrade prompt. api/client
// announces every 403 plan_upgrade_required and 429 ai_allowance_exceeded;
// this shows the ones the user caused (a save, a download, an AI request) as
// a dialog with the server's own sentence. Background reads are left to the
// page. Mounted once, in AppLayout. It opens without a DialogTrigger, so it
// hands focus back itself: to whatever had it when the refusal arrived (the
// button that made the request), if that's still on the page.

export function UpgradePrompt() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { plan } = useEntitlements();
  // `limit` outlives `open` so the content stays put while the dialog
  // animates out.
  const [limit, setLimit] = useState<PlanLimitDetail | null>(null);
  const [open, setOpen] = useState(false);
  const openRef = useRef(false);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const onLimit = (e: Event) => {
      const detail = (e as CustomEvent<PlanLimitDetail>).detail;
      if (!detail || detail.background) return;
      // One at a time: a burst of refusals from the same click shows once.
      if (openRef.current) return;
      openRef.current = true;
      returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setLimit(detail);
      setOpen(true);
    };
    window.addEventListener(PLAN_LIMIT_EVENT, onLimit);
    return () => window.removeEventListener(PLAN_LIMIT_EVENT, onLimit);
  }, []);

  if (!limit) return null;

  const { prompt } = plansCopy;
  const isAdmin = canManageBilling(user);
  const isTranscripts = limit.code === 'transcript_limit_reached';
  const isAllowance = limit.code === 'ai_allowance_exceeded' || isTranscripts;
  const key = entitlementKey(limit.entitlement);
  const feature = key ? ENTITLEMENT_LABELS[key] : undefined;
  const resets = formatResetDate(limit.resetsAt);

  const title = isTranscripts
    ? prompt.transcriptsTitle
    : isAllowance
    ? prompt.allowanceTitle
    : feature ? fill(feature.plural ? prompt.titlePlural : prompt.title, { feature: feature.label }) : prompt.titleGeneric;
  const message = limit.message || (isAllowance
    ? prompt.allowanceBody
    : limit.requiredPlan ? fill(prompt.needsPlan, { plan: limit.requiredPlan }) : prompt.needsGeneric);
  const planLine = isAllowance ? null : currentPlanSentence(plan);

  const close = () => {
    openRef.current = false;
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) close(); }}>
      <DialogContent
        className="max-w-md"
        onCloseAutoFocus={(e) => {
          const target = returnFocusRef.current;
          returnFocusRef.current = null;
          if (target && target.isConnected && target !== document.body) {
            e.preventDefault();
            target.focus();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>
        {(resets || planLine || !isAdmin) && (
          <div className="flex flex-col gap-1 text-sm text-muted-foreground">
            {isAllowance && resets && <p>{fill(prompt.allowanceResets, { date: resets })}</p>}
            {planLine && <p>{planLine}</p>}
            {!isAdmin && <p>{plansCopy.askAdmin}</p>}
          </div>
        )}
        <DialogFooter>
          {isAdmin ? (
            <>
              <Button variant="ghost" onClick={close}>{plansCopy.notNow}</Button>
              <Button onClick={() => { close(); navigate('/billing'); }}>{plansCopy.seePlans}</Button>
            </>
          ) : (
            <Button onClick={close}>{plansCopy.ok}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default UpgradePrompt;
